import { MyScheduleState, MyScheduleAction, ActionType } from './types'

export const initialState: MyScheduleState = {
  // Filter selections
  selectedOrganization: null,
  selectedSchedule: null,
  selectedTeam: null,
  selectedVenue: null,
  selectedFacility: null,
  selectedDateRange: null,
  selectedTimePeriod: 'TODAY',
  selectedMembershipType: 'following',
  selectedSortOrder: 'asc',

  // Filter options
  organizationsTree: null,
  schedules: [],
  teams: [],
  venues: [],

  // Filter pagination
  schedulesPagination: null,
  teamsPagination: null,
  venuesPagination: null,

  // Filter search queries
  schedulesSearchQuery: '',
  teamsSearchQuery: '',
  venuesSearchQuery: '',

  // Filter appending states
  appendingSchedules: false,
  appendingTeams: false,
  appendingVenues: false,

  // Games data
  games: [],
  gamesPagination: null,

  // Dates data
  dates: [],
  hasTodayGames: false,
  hasUpcomingGames: false,
  datesLoaded: false,

  // Loading states
  loadingGames: false,
  loadingOrganizations: false,
  loadingSchedules: false,
  loadingTeams: false,
  loadingVenues: false,
  loadingDates: false,
  filtersLoaded: false,
}

export const myScheduleReducer = (
  state: MyScheduleState,
  action: MyScheduleAction
): MyScheduleState => {
  console.log('🔄 MyScheduleReducer:', action.type)

  switch (action.type) {
    case ActionType.SET_SELECTED_ORGANIZATION:
      return {
        ...state,
        selectedOrganization: action.payload,
        // Clear schedule if it doesn't belong to new organization
        selectedSchedule:
          action.payload &&
          state.selectedSchedule?.organization_id !== action.payload.id
            ? null
            : state.selectedSchedule,
        // Clear team if organization changed
        selectedTeam:
          action.payload &&
          state.selectedTeam?.organization_id !== action.payload.id
            ? null
            : state.selectedTeam,
      }

    case ActionType.SET_SELECTED_SCHEDULE:
      return {
        ...state,
        selectedSchedule: action.payload,
      }

    case ActionType.SET_SELECTED_TEAM:
      return {
        ...state,
        selectedTeam: action.payload,
      }

    case ActionType.SET_SELECTED_VENUE:
      return {
        ...state,
        selectedVenue: action.payload,
        // Clear facility when venue changes (new venue may have different facilities)
        selectedFacility: null,
      }

    case ActionType.SET_SELECTED_FACILITY:
      return {
        ...state,
        selectedFacility: action.payload,
      }

    case ActionType.SET_SELECTED_DATE_RANGE:
      return {
        ...state,
        selectedDateRange: action.payload,
        // When date range is set, clear time period
        selectedTimePeriod: action.payload ? 'TODAY' : state.selectedTimePeriod,
      }

    case ActionType.SET_SELECTED_TIME_PERIOD:
      return {
        ...state,
        selectedTimePeriod: action.payload,
        // When time period is set, clear date range
        selectedDateRange: null,
        // Set default sort: PAST → desc, TODAY/UPCOMING → asc
        selectedSortOrder: action.payload === 'PAST' ? 'desc' : 'asc',
      }

    case ActionType.SET_ORGANIZATIONS_TREE:
      return {
        ...state,
        organizationsTree: action.payload,
      }

    case ActionType.SET_SCHEDULES:
      return {
        ...state,
        schedules: action.payload.data,
        schedulesPagination: action.payload.pagination,
      }

    case ActionType.SET_TEAMS:
      return {
        ...state,
        teams: action.payload.data,
        teamsPagination: action.payload.pagination,
      }

    case ActionType.SET_VENUES:
      return {
        ...state,
        venues: action.payload.data,
        venuesPagination: action.payload.pagination,
      }

    case ActionType.SET_GAMES:
      return {
        ...state,
        games: action.payload.games,
        gamesPagination: action.payload.pagination,
      }

    case ActionType.APPEND_GAMES:
      return {
        ...state,
        games: [...state.games, ...action.payload.games],
        gamesPagination: action.payload.pagination,
      }

    case ActionType.SET_LOADING_GAMES:
      return {
        ...state,
        loadingGames: action.payload,
      }

    case ActionType.SET_LOADING_ORGANIZATIONS:
      return {
        ...state,
        loadingOrganizations: action.payload,
      }

    case ActionType.SET_LOADING_SCHEDULES:
      return {
        ...state,
        loadingSchedules: action.payload,
      }

    case ActionType.SET_LOADING_TEAMS:
      return {
        ...state,
        loadingTeams: action.payload,
      }

    case ActionType.SET_LOADING_VENUES:
      return {
        ...state,
        loadingVenues: action.payload,
      }

    case ActionType.SET_LOADING_DATES:
      return {
        ...state,
        loadingDates: action.payload,
      }

    case ActionType.SET_DATES: {
      const defaultPeriod =
        action.payload.defaultTimePeriod ?? state.selectedTimePeriod
      return {
        ...state,
        dates: action.payload.dates,
        hasTodayGames: action.payload.hasTodayGames,
        hasUpcomingGames: action.payload.hasUpcomingGames,
        datesLoaded: true,
        selectedTimePeriod: defaultPeriod,
        selectedSortOrder: defaultPeriod === 'PAST' ? 'desc' : 'asc',
      }
    }

    case ActionType.CLEAR_FILTER: {
      const updates: Partial<MyScheduleState> = {}

      switch (action.payload) {
        case 'organization':
          updates.selectedOrganization = null
          updates.selectedSchedule = null
          updates.selectedTeam = null
          break
        case 'schedule':
          updates.selectedSchedule = null
          break
        case 'team':
          updates.selectedTeam = null
          break
        case 'venue':
          updates.selectedVenue = null
          updates.selectedFacility = null
          break
        case 'facility':
          updates.selectedFacility = null
          break
        case 'dateRange':
          updates.selectedDateRange = null
          break
        case 'timePeriod':
          updates.selectedTimePeriod = 'TODAY'
          break
      }

      return {
        ...state,
        ...updates,
      }
    }

    case ActionType.CLEAR_ALL_FILTERS:
      return {
        ...state,
        selectedOrganization: null,
        selectedSchedule: null,
        selectedTeam: null,
        selectedVenue: null,
        selectedFacility: null,
        selectedDateRange: null,
        selectedTimePeriod: 'TODAY',
        selectedMembershipType: 'following',
      }

    case ActionType.SET_SELECTED_MEMBERSHIP_TYPE:
      return {
        ...state,
        selectedMembershipType: action.payload,
        // Clear all dependent filters when membership type changes
        selectedOrganization: null,
        selectedSchedule: null,
        selectedTeam: null,
        selectedVenue: null,
        selectedFacility: null,
      }

    case ActionType.APPEND_SCHEDULES:
      return {
        ...state,
        schedules: [...state.schedules, ...action.payload.data],
        schedulesPagination: action.payload.pagination,
      }

    case ActionType.APPEND_TEAMS:
      return {
        ...state,
        teams: [...state.teams, ...action.payload.data],
        teamsPagination: action.payload.pagination,
      }

    case ActionType.SET_APPENDING_SCHEDULES:
      return {
        ...state,
        appendingSchedules: action.payload,
      }

    case ActionType.SET_APPENDING_TEAMS:
      return {
        ...state,
        appendingTeams: action.payload,
      }

    case ActionType.SET_SCHEDULES_SEARCH_QUERY:
      return {
        ...state,
        schedulesSearchQuery: action.payload,
      }

    case ActionType.SET_TEAMS_SEARCH_QUERY:
      return {
        ...state,
        teamsSearchQuery: action.payload,
      }

    case ActionType.APPEND_VENUES:
      return {
        ...state,
        venues: [...state.venues, ...action.payload.data],
        venuesPagination: action.payload.pagination,
      }

    case ActionType.SET_APPENDING_VENUES:
      return {
        ...state,
        appendingVenues: action.payload,
      }

    case ActionType.SET_VENUES_SEARCH_QUERY:
      return {
        ...state,
        venuesSearchQuery: action.payload,
      }

    case ActionType.SET_SELECTED_SORT_ORDER:
      return {
        ...state,
        selectedSortOrder: action.payload,
      }

    case ActionType.SET_FILTERS_LOADED:
      return {
        ...state,
        filtersLoaded: action.payload,
      }

    default:
      return state
  }
}
